/**
 * The typed check-in (D-018): a student without their badge finds themself by
 * student number or name, confirms "that's me", and is checked in through the
 * same /scan endpoint as a badge. Nothing is written until they confirm.
 *
 * The scan screen's second door, so it wears the same dark band and renders
 * the same result cards. On a scanner device it is never the resting state:
 * every result, and 45 seconds of nobody touching it, hands the lobby back to
 * `/`. On a type-mode device (D-019) it *is* the resting state — `/` redirects
 * here — so the same moments clear the screen back to its own empty field
 * instead, and there is no "Back to scan".
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type FormEvent,
  type PointerEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import {
  BADGE_PATTERN,
  checkInByStudentId,
  findStudents,
  formatTimeRange,
  fullNameOf,
  scan,
  type ScanResult,
  type StudentMatch,
} from "../api/kiosk";
import { CHOICE_DISMISS_MS, DISMISS_MS, ResultCard } from "../components/ResultCard";
import GearLink from "../components/GearLink";
import { useDevice } from "../context/DeviceContext";
import { clockLine, useClock } from "../hooks/useClock";
import { useOnline } from "../hooks/useOnline";
import { useScannerInput } from "../hooks/useScannerInput";
import { useSettledClick } from "../hooks/useSettledClick";
import "./ScanScreen.css";
import "./FindScreen.css";

/** Name search starts here — the server answers `[]` below it anyway. */
const MIN_NAME_CHARS = 3;
const NAME_DEBOUNCE_MS = 300;
/** Untouched this long, the screen goes back to the kiosk's resting state. */
const IDLE_MS = 45_000;

/**
 * BADGE_PATTERN without its start anchor. A wedge scan lands wherever the
 * caret is, so a code scanned into a half-typed name arrives as "adaZB7K…" —
 * the badge is whatever the field *ends* with.
 */
const TRAILING_BADGE = new RegExp(BADGE_PATTERN.source.replace(/^\^/, ""));

const NUMBER_NOT_FOUND = "Student not found — check the number or see the front desk";
const NAME_NOT_FOUND = "No one by that name — check the spelling or see the front desk";
const NAME_TOO_SHORT = "Type at least 3 letters — add your last name if yours is short";

/**
 * How long the confirm card's buttons, and the class choices after a typed
 * check-in, ignore taps after they appear — the second tap of a double-tap
 * lands well inside it, a deliberate tap well outside (useSettledClick).
 */
const TAP_SETTLE_MS = 400;

/** A blur this soon after a mouse press was caused by that press. */
const POINTER_BLUR_MS = 1000;

/**
 * At or below this *visual* viewport height the screen is short — in practice,
 * a tablet keyboard is up — and the typing layout compacts (FindScreen.css,
 * `[data-short]`). A media query can't see it: it reads the layout viewport,
 * which iOS doesn't shrink for the keyboard.
 */
const SHORT_VIEWPORT_PX = 480;

/** Breathing room above the field when new results scroll it to the top. */
const FORM_SCROLL_MARGIN_PX = 8;

type Phase =
  | { name: "find" }
  | { name: "confirm"; student: StudentMatch }
  | { name: "working" }
  /** `typed`: a typed check-in (name or number) — the choices get the tap guard. */
  | { name: "result"; result: ScanResult; typed: boolean };

/** What was submitted, so an ambiguous choice can re-submit it with a batch id. */
type Pending =
  { kind: "badge"; badge: string } | { kind: "student"; studentUserId: number };

function isStudentNumber(value: string): boolean {
  return /^\d+$/.test(value);
}

export default function FindScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { device, evict, checkInMode } = useDevice();
  const { online, reportRequest } = useOnline();
  const now = useClock();
  // No scanner at this branch: this screen is home, and "home" is its own
  // empty field rather than `/` (which would only redirect back here).
  const typeMode = checkInMode === "type";
  // Where "home" is on a scanner device: `/`, unless the student came from the
  // classic screen at `/scan` (D-019), which a kiosk may be parked on whatever
  // the device's mode. Only that one value is honoured — history state is not
  // a place to take arbitrary destinations from.
  const home =
    (location.state as { home?: unknown } | null)?.home === "/scan" ? "/scan" : "/";

  const [phase, setPhase] = useState<Phase>({ name: "find" });
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<StudentMatch[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Lookups: one debounce, one in-flight request, and a sequence number so a
  // slow answer for "Ad" can never paint over the answer for "Ada".
  const debounceRef = useRef(0);
  const lookupAbortRef = useRef<AbortController | null>(null);
  const lookupSeqRef = useRef(0);

  // Check-ins: same guard as ScanScreen's scanSeqRef.
  const scanSeqRef = useRef(0);
  const dismissTimerRef = useRef(0);
  const pendingRef = useRef<Pending | null>(null);

  // Requests of either kind still out. The idle timeout never fires under one —
  // a student waiting on a slow answer is not idle.
  const inFlightRef = useRef(0);
  const idleTimerRef = useRef(0);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = 0;
    }
  }, []);

  /** Drop any pending or in-flight lookup; whatever it would have said is stale. */
  const abandonLookup = useCallback(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = 0;
    lookupAbortRef.current?.abort();
    lookupAbortRef.current = null;
    lookupSeqRef.current += 1;
  }, []);

  /**
   * Back to the kiosk's resting state: the scan screen it came from, or — in type mode —
   * this screen's own idle state, with the field, list and notice cleared so
   * the next student never sees the last one's name.
   */
  const goHome = useCallback(() => {
    if (!typeMode) {
      navigate(home, { replace: true });
      return;
    }
    abandonLookup();
    window.clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = 0;
    pendingRef.current = null;
    setQuery("");
    setMatches([]);
    setNotice(null);
    setSearching(false);
    setPhase({ name: "find" });
  }, [abandonLookup, home, navigate, typeMode]);

  const armIdle = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    const expire = () => {
      // Never under a request, and never under a visible result: the result's
      // own dismiss timer decides when it goes, so it always gets its full time.
      if (inFlightRef.current > 0 || dismissTimerRef.current) {
        idleTimerRef.current = window.setTimeout(expire, IDLE_MS);
        return;
      }
      goHome();
    };
    idleTimerRef.current = window.setTimeout(expire, IDLE_MS);
  }, [goHome]);

  // Leaving by any route tears everything down, and bumps the check-in
  // sequence so a response landing after unmount is ignored rather than
  // arming a dismiss timer that navigates from wherever the kiosk now is.
  const teardown = useCallback(() => {
    abandonLookup();
    clearDismissTimer();
    scanSeqRef.current += 1;
  }, [abandonLookup, clearDismissTimer]);

  useEffect(() => teardown, [teardown]);

  // Separate from the teardown so a re-arm can never cancel a check-in.
  const clearIdle = useCallback(() => window.clearTimeout(idleTimerRef.current), []);
  useEffect(() => {
    armIdle();
    return clearIdle;
  }, [armIdle, clearIdle]);

  const evictDevice = useCallback(() => {
    evict("This device needs to be re-registered.");
    navigate("/setup", { replace: true });
  }, [evict, navigate]);

  /* --------------------------------------------------------------- lookup */

  const lookup = useCallback(
    async (q: string) => {
      const token = device?.kioskToken;
      if (!token) {
        navigate("/setup", { replace: true });
        return;
      }

      abandonLookup();
      // Whatever the last attempt said ("Can't reach the server") is about to
      // be out of date; it must not sit over "Looking…" or outlive a retry.
      setNotice(null);
      const seq = lookupSeqRef.current;
      const controller = new AbortController();
      lookupAbortRef.current = controller;
      inFlightRef.current += 1;
      setSearching(true);

      try {
        const rows = await findStudents(token, q, controller.signal);
        if (seq !== lookupSeqRef.current) return; // superseded by more typing
        reportRequest(true);
        if (isStudentNumber(q)) {
          // An exact number still goes through the confirm card — a typo must
          // not check in somebody else's child. More than one row should not
          // happen for an id, but if it does it is a choice, not a miss.
          setMatches(rows.length > 1 ? rows : []);
          setNotice(rows.length === 0 ? NUMBER_NOT_FOUND : null);
          if (rows.length === 1) setPhase({ name: "confirm", student: rows[0] });
        } else {
          setMatches(rows);
          setNotice(rows.length ? null : NAME_NOT_FOUND);
        }
      } catch (error) {
        if (seq !== lookupSeqRef.current) return;
        if (error instanceof ApiError && error.isInvalidDevice) {
          evictDevice();
          return;
        }
        // Only a request that never got an answer says anything about the
        // connection; a 429 or a 500 still reached the server.
        const unreachable = !(error instanceof ApiError) || error.isNetwork;
        reportRequest(!unreachable);
        // The old list answered an older query; under an error it would read
        // as this one's answer.
        setMatches([]);
        // A 429 gets its own copy: the whole lobby shares one IP, so "try
        // again" straight away would just be refused again.
        const limited = error instanceof ApiError && error.httpStatus === 429;
        setNotice(
          unreachable
            ? "Can't reach the server — try again, or see the front desk."
            : limited
              ? "Too many searches — wait a moment and try again."
              : "Search isn't working right now — try again, or see the front desk.",
        );
      } finally {
        inFlightRef.current -= 1;
        if (seq === lookupSeqRef.current) {
          lookupAbortRef.current = null;
          setSearching(false);
        }
      }
    },
    [abandonLookup, device?.kioskToken, evictDevice, navigate, reportRequest],
  );

  const handleChange = useCallback(
    (value: string) => {
      setQuery(value);
      setNotice(null);
      abandonLookup();
      setSearching(false);

      const q = value.trim();
      if (isStudentNumber(q)) {
        // Numbers are looked up on Find/Enter only, never on a pause: "254" on
        // the way to "25452" is somebody else's number, and a confirm card
        // popping up mid-number would also swallow the next keystrokes.
        setMatches([]);
      } else if (q.length >= MIN_NAME_CHARS) {
        // The current list stays up until the new one lands — no flicker per key.
        debounceRef.current = window.setTimeout(() => void lookup(q), NAME_DEBOUNCE_MS);
      } else {
        setMatches([]);
      }
    },
    [abandonLookup, lookup],
  );

  /* -------------------------------------------------------------- check-in */

  const showResult = useCallback(
    (result: ScanResult, typed = false) => {
      clearDismissTimer();
      setPhase({ name: "result", result, typed });
      const ms = result.kind === "ambiguous" ? CHOICE_DISMISS_MS : DISMISS_MS;
      dismissTimerRef.current = window.setTimeout(() => {
        // Fired: no longer a visible result for the idle timer to wait on.
        dismissTimerRef.current = 0;
        pendingRef.current = null;
        goHome();
      }, ms);
    },
    [clearDismissTimer, goHome],
  );

  const submit = useCallback(
    async (pending: Pending, batchId?: number) => {
      const token = device?.kioskToken;
      if (!token) {
        navigate("/setup", { replace: true });
        return;
      }

      const seq = ++scanSeqRef.current;
      pendingRef.current = pending;
      abandonLookup();
      clearDismissTimer();
      setPhase({ name: "working" });
      inFlightRef.current += 1;

      try {
        const result =
          pending.kind === "badge"
            ? await scan(token, pending.badge, batchId)
            : await checkInByStudentId(token, pending.studentUserId, batchId);
        if (seq !== scanSeqRef.current) return;
        reportRequest(result.kind !== "unavailable");
        showResult(result, pending.kind === "student");
      } catch (error) {
        if (seq !== scanSeqRef.current) return;
        // Both calls throw only for a rejected device token; everything else
        // comes back as a renderable result.
        if (error instanceof ApiError && error.isInvalidDevice) {
          evictDevice();
          return;
        }
        reportRequest(false);
        showResult({
          kind: "unavailable",
          message: "Connection problem — try again or see the coach.",
        });
      } finally {
        inFlightRef.current -= 1;
      }
    },
    [
      abandonLookup,
      clearDismissTimer,
      device?.kioskToken,
      evictDevice,
      navigate,
      reportRequest,
      showResult,
    ],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // Read the field itself, not state: a wedge scanner's Enter follows its
      // last character by a few milliseconds.
      const q = (inputRef.current?.value ?? "").trim();
      // A badge scanned into this box: the door keeps working from here.
      // Upper-cased first: the field capitalises words, so a code typed by
      // hand arrives as "Zb7k…". Codes are upper case by construction (D-003).
      const badge = TRAILING_BADGE.exec(q.toUpperCase())?.[0];
      if (badge) {
        void submit({ kind: "badge", badge });
        return;
      }
      if (isStudentNumber(q) || q.length >= MIN_NAME_CHARS) {
        void lookup(q);
        return;
      }
      if (q) {
        abandonLookup();
        setSearching(false);
        setMatches([]);
        setNotice(NAME_TOO_SHORT);
      }
    },
    [abandonLookup, lookup, submit],
  );

  const handlePick = useCallback(
    (student: StudentMatch) => {
      abandonLookup();
      setSearching(false);
      setPhase({ name: "confirm", student });
    },
    [abandonLookup],
  );

  const handleChoice = useCallback(
    (batchId: number) => {
      const pending = pendingRef.current;
      if (pending) void submit(pending, batchId);
    },
    [submit],
  );

  /*
    Badge scans whenever the text field does not have focus — the confirm
    card, a result, "Checking…", or the find phase with the field blurred or
    a pick-list button Tab-focused — go through the same hook ScanScreen
    uses. Off only while the field itself is focused: the hook
    preventDefaults every document-level Enter, which would kill the form's
    submit, and the focused field catches scans itself. No sink input is
    rendered; the hook's document listener doesn't need one.
  */
  const [fieldFocused, setFieldFocused] = useState(false);
  /*
    Kept honest from the document, not only from the field's own focus/blur:
    when the panel unmounts with the field focused (Enter on a number, a badge
    scanned into it) React delivers no blur, and if the field then can't take
    focus back when the panel returns — iOS refuses a timer-driven autoFocus
    without a gesture — a stale `true` would leave the hook off and drop every
    scan until somebody taps. So: focus landing anywhere else clears it, and so
    does the field leaving the page (the callback ref below).
  */
  useEffect(() => {
    const onFocusIn = (event: globalThis.FocusEvent) =>
      setFieldFocused(event.target !== null && event.target === inputRef.current);
    const onFocusOut = (event: globalThis.FocusEvent) => {
      if (event.target === inputRef.current) setFieldFocused(false);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  const attachInput = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (!node) setFieldFocused(false);
  }, []);
  const handleScan = useCallback(
    (badge: string) => void submit({ kind: "badge", badge }),
    [submit],
  );
  const handleSlowScan = useCallback(
    () =>
      showResult({
        kind: "slow_scan",
        message: "Scan read too slowly — hold steady and scan again.",
      }),
    [showResult],
  );
  useScannerInput({
    onScan: handleScan,
    onSlowScan: handleSlowScan,
    enabled: phase.name !== "find" || !fieldFocused,
  });

  // A tap on the background blurs the field, and a scan into a blurred field
  // goes nowhere. Put the caret back — after the browser's own focus change,
  // hence the timeout — unless the tap was on a control, or anywhere in the
  // pick list: a scroll drag that starts in a gap between names must not
  // raise the keyboard back over the list being scrolled.
  const handleBackgroundPointer = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (phase.name !== "find") return;
      const target = event.target as Element | null;
      if (target?.closest("button, a, input, select, textarea, .find-matches")) return;
      window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
    },
    [phase.name],
  );

  /*
    Whether to take focus back after a blur is decided by what caused the
    blur, not by what kind of device this is — a touchscreen laptop is both.
    A mouse click that leaves focus on nothing is a laptop user clicking the
    background: put the caret back, there is no on-screen keyboard to raise.
    A touch or pen tap, the iPad's hide-keyboard key, Tab, or the window
    losing focus: leave it — a dismissed keyboard stays dismissed. Scans are
    safe either way, because the hook above listens whenever the field isn't
    focused. (A touch tap on the background still asks for the field back —
    handleBackgroundPointer.)
  */
  const lastPointerRef = useRef({ type: "", at: 0 });
  const notePointer = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      lastPointerRef.current = { type: event.pointerType, at: event.timeStamp };
      armIdle();
    },
    [armIdle],
  );

  const handleFieldBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    setFieldFocused(false);
    const pointer = lastPointerRef.current;
    const byMouse =
      pointer.type === "mouse" && event.timeStamp - pointer.at < POINTER_BLUR_MS;
    if (!byMouse) return;
    window.setTimeout(() => {
      const active = document.activeElement;
      if (!active || active === document.body) {
        inputRef.current?.focus({ preventScroll: true });
      }
    }, 0);
  }, []);

  // New results scroll the *field* to the top of the stage, so the list sits
  // directly under it: with a tablet keyboard up there is room for little
  // else, and the student has to see what they typed next to the answers.
  // The stage alone is scrolled — scrollIntoView() would scroll every
  // scrollable ancestor too, and on iOS can pan the visual viewport, pushing
  // the topbar off the top of the glass.
  const stageRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const stage = stageRef.current;
    const form = formRef.current;
    if (matches.length === 0 || !stage || !form) return;
    const offset = form.getBoundingClientRect().top - stage.getBoundingClientRect().top;
    stage.scrollTo({
      top: Math.max(0, stage.scrollTop + offset - FORM_SCROLL_MARGIN_PX),
      behavior: "smooth",
    });
  }, [matches]);

  /*
    iOS doesn't shrink the layout viewport for its keyboard — only the visual
    viewport — so a 100%-tall screen runs on underneath the keys. Mirror the
    visual viewport's height into --find-vh on the root and size the screen
    from it, so the stage (the flex child between topbar and footer) and its
    scroll area end at the top of the keyboard. Android and desktop get the
    same number they would anyway (index.html asks for resizes-content).
    The same height sets `data-short` on the root, which compacts the typing
    layout — the one signal that is true on iPad, Android and desktop alike.
  */
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = rootRef.current;
    if (!viewport || !root) return;
    const sync = () => {
      root.style.setProperty("--find-vh", `${viewport.height}px`);
      root.toggleAttribute("data-short", viewport.height <= SHORT_VIEWPORT_PX);
    };
    sync();
    viewport.addEventListener("resize", sync);
    viewport.addEventListener("scroll", sync);
    return () => {
      viewport.removeEventListener("resize", sync);
      viewport.removeEventListener("scroll", sync);
    };
  }, []);

  const typingNumber = isStudentNumber(query.trim());

  return (
    <div
      ref={rootRef}
      className={`screen scan find${phase.name === "find" ? " is-typing" : ""}`}
      // Any touch or keystroke on this screen is activity. `input` as well as
      // `keydown`: some on-screen keyboards report every key as a bare 229.
      onPointerDownCapture={notePointer}
      onKeyDownCapture={armIdle}
      onInputCapture={armIdle}
      onPointerDown={handleBackgroundPointer}
    >
      <div className="zebra-field" aria-hidden="true" />

      <header className="topbar">
        {/* Type mode makes this the resting screen, so it carries the staff gear
            the scan screen otherwise does. */}
        {typeMode && <GearLink />}
        <span className="scan-branch">{device?.branchName || "Unregistered"}</span>
        <span className="spacer" />
        <ConnectionPill online={online} />
      </header>

      <main ref={stageRef} className="find-stage">
        {phase.name === "find" && (
          <div className="find-panel">
            <p className="eyebrow idle-eyebrow find-eyebrow">Zebra Robotics · Check-in</p>
            {/* The system's hero pattern: white display type with one word in gold. */}
            <h1 className="find-prompt display" id="find-prompt">
              Type your <span className="accent">name</span> or student number
            </h1>

            <form
              ref={formRef}
              className="find-form"
              onSubmit={handleSubmit}
              role="search"
            >
              <input
                ref={attachInput}
                className="find-input"
                type="text"
                value={query}
                onChange={(event) => handleChange(event.target.value)}
                onFocus={() => setFieldFocused(true)}
                onBlur={handleFieldBlur}
                aria-labelledby="find-prompt"
                aria-describedby="find-hint"
                placeholder="Start typing…"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="words"
                spellCheck={false}
                enterKeyHint="search"
              />
              {/*
                The visible way to submit: `enterKeyHint` is a hint, and not
                every on-screen keyboard shows a usable Enter. mousedown is
                cancelled so the tap doesn't pull focus (and a wedge scan's
                keystrokes) out of the field.
              */}
              <button
                type="submit"
                className="find-go"
                onMouseDown={(event) => event.preventDefault()}
              >
                Find
              </button>
            </form>

            <p className="find-hint" id="find-hint">
              {typingNumber ? "Press Find" : "Your student number is on your badge card."}
            </p>

            {/* Always rendered, empty when quiet: a live region that appears
                with its first message is often not announced. */}
            <p className="find-note" role="status">
              {notice ?? (matches.length === 0 && searching ? "Looking…" : "")}
            </p>

            {matches.length > 0 && (
              <ul
                className="find-matches"
                aria-label="Pick your name"
                aria-busy={searching}
              >
                {matches.map((student, index) => (
                  <li
                    key={student.student_user_id}
                    // Rows rise in one after another; a row that stays put
                    // across searches keeps its key and doesn't replay.
                    style={{ "--row": index } as CSSProperties}
                  >
                    <button
                      type="button"
                      className="find-match"
                      onClick={() => handlePick(student)}
                    >
                      <span className="find-match-name">
                        {fullNameOf(student.firstname, student.lastname)}
                      </span>
                      <ClassLine student={student} className="find-match-class" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <p className="idle-clock find-clock">{clockLine(now)}</p>
          </div>
        )}

        {phase.name === "confirm" && (
          <Confirm
            student={phase.student}
            onYes={() =>
              void submit({
                kind: "student",
                studentUserId: phase.student.student_user_id,
              })
            }
            onNo={() => setPhase({ name: "find" })}
          />
        )}

        {phase.name === "working" && (
          <p className="working" role="status">
            Checking…
          </p>
        )}

        {phase.name === "result" && (
          <ResultCard
            result={phase.result}
            onChoose={handleChoice}
            settleMs={phase.typed ? TAP_SETTLE_MS : 0}
          />
        )}
      </main>

      {/*
        Back sits on the right of the footer, where the scan screen has only
        its inert brand lockup — so the second tap of a double-tap on Back
        lands on nothing. Not in the topbar (the scan screen's setup gear is
        there: a staff login) nor on the left of the footer (its "Today's
        classes": a board with no scanner and no way back on its own). Not
        while a check-in is in flight: its result must be seen. In type mode
        there is nowhere to go back to, so the footer is the scan screen's.
      */}
      {typeMode ? (
        // The resting screen's footer, laid out as the scan screen's is.
        <footer className="scan-footer">
          <button type="button" className="btn-ghost" onClick={() => navigate("/today")}>
            Today&rsquo;s classes
          </button>
          <span className="spacer" />
          <img
            className="brand-lockup"
            src="/brand/logo-horizontal.png"
            alt="Zebra Robotics"
          />
        </footer>
      ) : (
        <footer className="scan-footer">
          <img
            className="brand-lockup"
            src="/brand/logo-horizontal.png"
            alt="Zebra Robotics"
          />
          <span className="spacer" />
          <button
            type="button"
            className="btn-ghost btn-ghost--student"
            onClick={goHome}
            disabled={phase.name === "working"}
          >
            {home === "/scan" ? "← Back to scan" : "← Back"}
          </button>
        </footer>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ pieces */

/**
 * "Check in as Ada Lovelace?", with the class she is about to be marked in —
 * the one step between a lookup and a write. A light
 * band dropped into the dark screen, like the ambiguous-class question, so a
 * question never looks like a result.
 *
 * The field it replaces held focus, so the card takes it — the container, not
 * a button: a stray Enter must never answer the question. Its buttons also
 * ignore taps for TAP_SETTLE_MS after it appears: a double-tap on a name in
 * the pick list would otherwise land its second tap on "Yes" and check the
 * student in without anyone having confirmed anything.
 */
function Confirm({
  student,
  onYes,
  onNo,
}: {
  student: StudentMatch;
  onYes: () => void;
  onNo: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, []);
  const settled = useSettledClick(TAP_SETTLE_MS);

  return (
    <div
      ref={cardRef}
      className="result is-ask find-confirm"
      role="group"
      aria-labelledby="find-confirm"
      tabIndex={-1}
    >
      <h1 className="find-confirm-question" id="find-confirm">
        <span className="find-confirm-lede">Check in as</span>
        <span className="find-confirm-name">
          <span className="find-confirm-mark">
            {fullNameOf(student.firstname, student.lastname)}
          </span>
          ?
        </span>
      </h1>
      <ClassLine student={student} className="find-confirm-class" />
      <div className="find-confirm-actions">
        <button
          type="button"
          className="find-confirm-yes"
          onClick={(event) => {
            if (settled(event)) onYes();
          }}
        >
          Yes, that&rsquo;s me
        </button>
        <button
          type="button"
          className="find-confirm-no"
          onClick={(event) => {
            if (settled(event)) onNo();
          }}
        >
          No, go back
        </button>
      </div>
    </div>
  );
}

/**
 * What the student has today, on one line: "Lego Robotics · 4:00 PM–5:30 PM"
 * with a Makeup tag, else "Competitions", else "No class right now". The list
 * row and the confirm card say the same thing, sized differently.
 */
function ClassLine({ student, className }: { student: StudentMatch; className: string }) {
  const session = student.session_today;
  if (session) {
    // Start alone when the batch is open-ended (end_time null).
    const time = formatTimeRange(session.start_time, session.end_time);
    return (
      <span className={className}>
        {session.label && (
          <span className="class-label">
            {session.label}
            {/* The separator rides on the label, so a wrapped line never
                starts with a stray dot. */}
            {time && <span aria-hidden="true"> ·</span>}
          </span>
        )}
        {time && <span className="class-time">{time}</span>}
        {session.makeup && <span className="class-tag">Makeup</span>}
      </span>
    );
  }
  return (
    <span className={`${className} is-quiet`}>
      <span className="class-label">
        {student.competition ? "Competitions" : "No class right now"}
      </span>
    </span>
  );
}

/** Same as ScanScreen's; small enough to keep a copy rather than share. */
function ConnectionPill({ online }: { online: boolean }) {
  return (
    <span className={`pill${online ? "" : " is-offline"}`} role="status">
      <span className="pill-dot" aria-hidden="true" />
      {online ? "Online" : "Offline"}
    </span>
  );
}
